import { useState, useEffect, useContext } from 'react';
import { DashboardV4Data, DashboardV4DataWithContext, CHART_COLORS } from '../types/dashboard-v4.types';
import { DASHBOARD_MOCK_VALUES, MOCK_USER_DATA } from '../constants/dashboard-v4.constants';
import { dashboardV4Service } from '../services/dashboardV4Service';
import { useJunoContext } from '../contexts/JunoContext';
import { useAuth } from '../contexts/AuthContext';

export const useDashboardDataV4 = () => {
  const [data, setData] = useState<DashboardV4DataWithContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { updateViewContext } = useJunoContext();
  const { user } = useAuth();

  useEffect(() => {
    // Tracking variable to prevent state updates after unmount
    let isMounted = true;
    
    const fetchDashboardData = async () => {
      try {
        if (isMounted) setLoading(true);
        
        // Try to fetch real data
        try {
          console.log('Fetching dashboard data...');
          const response = await dashboardV4Service.fetchDashboardData();
          
          // Only update state if component is still mounted
          if (isMounted) {
            console.log('Dashboard data fetched successfully');
            const dashboardData: DashboardV4DataWithContext = {
              ...response.data,
              junoContext: response.junoContext
            };
            
            setData(dashboardData);
            
            // Update Juno context with dashboard data
            updateViewContext('today', {
              dashboardSummary: response.junoContext
            });
          }
          
        } catch (apiError) {
          console.warn('Failed to fetch real data, falling back to mock data:', apiError);
          
          // Fallback to mock data if API fails
          const userName = user?.name || user?.username || 'User';
          const mockData: DashboardV4Data = {
          user: {
            name: userName,
            lastSessionTime: MOCK_USER_DATA.LAST_SESSION_TIME
          },
          
          whatsChanged: [
            {
              id: '1',
              count: 3,
              title: 'Signals are now critically severe',
              description: 'Includes references to redress and formal escalation (e.g. ombudsman)'
            },
            {
              id: '2', 
              count: 1,
              title: 'Case updated with new signs of vulnerability',
              description: 'Flagged from new data with emotional strain indicators'
            },
            {
              id: '3',
              count: 2,
              title: 'Complaints approaching SLA deadlines',
              description: 'No updates logged in past 48 hours'
            }
          ],
          
          whatNeedsAttention: [
            {
              id: '1',
              type: 'critical',
              title: 'Critically severe signal not flagged as formal complaint',
              description: 'Customer WhatsApp includes dissatisfaction, redress demand, and formal escalation threat',
              actionLabel: 'Review Urgently',
              actionPath: '/v3/vault/critical-1',
              signalId: 'critical-1',
              customerName: 'John Smith',
              severity: 85
            },
            {
              id: '2',
              type: 'high',
              title: 'Resolution rejection with potential legal action',
              description: 'Customer email rejects outcome and threatens legal action',
              actionLabel: 'Escalate Case',
              actionPath: '/v3/cases/escalate-1',
              signalId: 'escalate-1',
              customerName: 'Sarah Johnson',
              severity: 78
            },
            {
              id: '3',
              type: 'medium',
              title: 'High-risk complaint nearing SLA breach',
              description: 'Complaint AC-1862 has no update in 14 days',
              actionLabel: 'Resolve Urgently',
              actionPath: '/v3/cases/AC-1862',
              signalId: 'AC-1862',
              customerName: 'Michael Brown',
              severity: 65
            },
            {
              id: '4',
              type: 'medium',
              title: 'Vulnerable customer flagged with signs of severe strain',
              description: 'Call transcript includes distress signals and dissatisfaction with service delays',
              actionLabel: 'Action Case',
              actionPath: '/v3/vault/vulnerable-1',
              signalId: 'vulnerable-1',
              customerName: 'Emma Wilson',
              severity: 72,
              vulnerabilitySummary: 'Shows signs of emotional distress and financial strain'
            }
          ],
          
          totalOpenSignals: DASHBOARD_MOCK_VALUES.TOTAL_OPEN_SIGNALS,
          totalOpenCases: DASHBOARD_MOCK_VALUES.TOTAL_OPEN_CASES,
          averageConductScore: DASHBOARD_MOCK_VALUES.AVERAGE_CONDUCT_SCORE,
          
          signalsAndCasesOverTime: [
            { date: 'Mon', signals: DASHBOARD_MOCK_VALUES.SIGNALS_BY_DAY[0], cases: DASHBOARD_MOCK_VALUES.CASES_BY_DAY[0] },
            { date: 'Tue', signals: DASHBOARD_MOCK_VALUES.SIGNALS_BY_DAY[1], cases: DASHBOARD_MOCK_VALUES.CASES_BY_DAY[1] },
            { date: 'Wed', signals: DASHBOARD_MOCK_VALUES.SIGNALS_BY_DAY[2], cases: DASHBOARD_MOCK_VALUES.CASES_BY_DAY[2] },
            { date: 'Thu', signals: DASHBOARD_MOCK_VALUES.SIGNALS_BY_DAY[3], cases: DASHBOARD_MOCK_VALUES.CASES_BY_DAY[3] },
            { date: 'Fri', signals: DASHBOARD_MOCK_VALUES.SIGNALS_BY_DAY[4], cases: DASHBOARD_MOCK_VALUES.CASES_BY_DAY[4] },
            { date: 'Sat', signals: DASHBOARD_MOCK_VALUES.SIGNALS_BY_DAY[5], cases: DASHBOARD_MOCK_VALUES.CASES_BY_DAY[5] },
            { date: 'Sun', signals: DASHBOARD_MOCK_VALUES.SIGNALS_BY_DAY[6], cases: DASHBOARD_MOCK_VALUES.CASES_BY_DAY[6] }
          ],
          
          complaintDetection: [
            { 
              date: '10 June', 
              dissatisfaction: 25,  // No Complaint - based on taxonomy classification
              potential: 45,       // Potential Complaint - based on taxonomy classification
              formal: 30           // Formal Complaint - based on taxonomy classification
            },
            { 
              date: '11 June', 
              dissatisfaction: 20,  // No Complaint - based on taxonomy classification
              potential: 50,       // Potential Complaint - based on taxonomy classification
              formal: 30           // Formal Complaint - based on taxonomy classification
            },
            { 
              date: 'Today', 
              dissatisfaction: 15,  // No Complaint - based on taxonomy classification
              potential: 35,       // Potential Complaint - based on taxonomy classification
              formal: 50           // Formal Complaint - based on taxonomy classification
            }
          ],
          
          channelBreakdown: [
            { channel: 'Email', count: DASHBOARD_MOCK_VALUES.EMAIL_COUNT, color: CHART_COLORS.SECONDARY, icon: '📧' },
            { channel: 'Phone', count: DASHBOARD_MOCK_VALUES.PHONE_COUNT, color: CHART_COLORS.PRIMARY, icon: '📞' },
            { channel: 'Chat', count: DASHBOARD_MOCK_VALUES.CHAT_COUNT, color: '#EC4899', icon: '💬' },
            { channel: 'Social', count: DASHBOARD_MOCK_VALUES.SOCIAL_COUNT, color: '#8B5CF6', icon: '📱' }
          ],
          
          vulnerabilityDetection: {
            percentage: DASHBOARD_MOCK_VALUES.VULNERABILITY_PERCENTAGE,
            signalCount: DASHBOARD_MOCK_VALUES.VULNERABILITY_SIGNAL_COUNT,
            weeklyChange: DASHBOARD_MOCK_VALUES.VULNERABILITY_WEEKLY_CHANGE
          },
          
          openCasesByAge: [
            { ageRange: '3 days', count: DASHBOARD_MOCK_VALUES.THREE_DAYS_COUNT, percentage: DASHBOARD_MOCK_VALUES.THREE_DAYS_PERCENTAGE, color: CHART_COLORS.PRIMARY },
            { ageRange: '3-8 wks', count: DASHBOARD_MOCK_VALUES.THREE_TO_EIGHT_WEEKS_COUNT, percentage: DASHBOARD_MOCK_VALUES.THREE_TO_EIGHT_WEEKS_PERCENTAGE, color: CHART_COLORS.SECONDARY },
            { ageRange: '8+ wks', count: DASHBOARD_MOCK_VALUES.EIGHT_PLUS_WEEKS_COUNT, percentage: DASHBOARD_MOCK_VALUES.EIGHT_PLUS_WEEKS_PERCENTAGE, color: CHART_COLORS.RED }
          ],
          
          topRootCauses: [
            { cause: 'Unclear Communication', count: DASHBOARD_MOCK_VALUES.UNCLEAR_COMMUNICATION_COUNT, percentage: DASHBOARD_MOCK_VALUES.UNCLEAR_COMMUNICATION_PERCENTAGE, color: '#7C3AED' },
            { cause: 'Billing Delay', count: DASHBOARD_MOCK_VALUES.BILLING_DELAY_COUNT, percentage: DASHBOARD_MOCK_VALUES.BILLING_DELAY_PERCENTAGE, color: CHART_COLORS.RED },
            { cause: 'Process Delay', count: DASHBOARD_MOCK_VALUES.PROCESS_DELAY_COUNT, percentage: DASHBOARD_MOCK_VALUES.PROCESS_DELAY_PERCENTAGE, color: CHART_COLORS.SECONDARY }
          ]
        };
        
          // Create mock Juno context
          const mockJunoContext = {
            summary: {
              totalSignals: DASHBOARD_MOCK_VALUES.TOTAL_OPEN_SIGNALS,
              criticalCount: 3,
              vulnerableCount: DASHBOARD_MOCK_VALUES.VULNERABILITY_SIGNAL_COUNT,
              slaRiskCount: 2
            },
            changes2weeks: {
              newCritical: 3,
              newVulnerable: 1,
              approachingSLA: 2
            },
            topConcerns: [
              {
                signalId: 'critical-1',
                summary: 'Critically severe signal not flagged as formal complaint',
                severity: 95,
                isVulnerable: false
              },
              {
                signalId: 'escalate-1',
                summary: 'Resolution rejection with potential legal action',
                severity: 85,
                isVulnerable: false
              },
              {
                signalId: 'vulnerable-1',
                summary: 'Vulnerable customer flagged with signs of severe strain',
                severity: 75,
                isVulnerable: true
              }
            ],
            timestamp: new Date().toISOString()
          };
          
          const mockDataWithContext: DashboardV4DataWithContext = {
            ...mockData,
            junoContext: mockJunoContext
          };
          
          setData(mockDataWithContext);
          
          // Update Juno context with mock data
          updateViewContext('today', {
            dashboardSummary: mockJunoContext
          });
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();
    
    // Cleanup function to prevent memory leaks and state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [updateViewContext, user]);

  return { data, loading, error };
};